import { getCultureFitVideoId, getInterviewVideoId } from './module-identificators';
import { getInternetSpeed } from './internet-quality';

// could be either video or image. timeoutMs guards against indefinite hangs
// (proxies that don't return a status, dropped connections, etc.). Default
// 8s is right for the small speed-test image; the video preload passes a
// longer timeout because the file itself is 50-60 MB and legitimately
// takes 20-30s on borderline-broadband connections.
export function preloadResource(resourceUrl, timeoutMs = 8000) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', resourceUrl, true);
        request.responseType = 'blob';
        request.timeout = timeoutMs;

        request.onload = (data) => {
            const status = data.target.status;
            if (status >= 200 && status < 300) {
                // Explicitly re-wrap the response with the correct MIME type.
                // iOS 18.7+ WebKit has a stricter blob-URL media decoder that
                // rejects blobs whose type doesn't match the actual content,
                // even when the server sent the correct Content-Type header.
                // Re-creating the Blob with an explicit type ensures the
                // video element always sees 'video/mp4'.
                const raw = data.target.response;
                const type =
                    raw.type && raw.type.startsWith('video/')
                        ? raw.type
                        : 'video/mp4';
                resolve(new Blob([raw], { type }));
                return;
            }
            // Any non-2xx status (304/403/404/5xx, captive portals, etc.)
            // must reject so callers don't await a promise that never settles.
            reject(
                new Error(
                    `preloadResource - HTTP ${status} for ${resourceUrl}`,
                ),
            );
        };

        request.onerror = (error) => {
            reject(error);
        };

        request.ontimeout = () => {
            reject(
                new Error(
                    `preloadResource - timeout after ${timeoutMs}ms for ${resourceUrl}`,
                ),
            );
        };

        request.onabort = () => {
            reject(
                new Error(`preloadResource - aborted for ${resourceUrl}`),
            );
        };

        request.send();
    });
}

export async function getVideosToPreload(modules) {
    let resolution;
    try {
        const internet = await getInternetSpeed();
        resolution = +internet.mbps < 100 ? 'low_res' : 'high_res';
    } catch (error) {
        // Speed test failed (most likely a slow/flaky connection that
        // timed out the speed-test image fetch). Default to low_res so
        // a slow user doesn't have to wait minutes for a high_res video.
        resolution = 'low_res';
    }

    const getVideosFromCultureFit = () => {
        const cultureFitModule = modules.find((module) => module.type === 'culture-fit-video');

        if (cultureFitModule) {
            return [
                { videoId: getCultureFitVideoId(), videoUrl: cultureFitModule.videos[resolution] },
            ];
        } else return [];
    };

    const getVideosFromInterviewModule = () => {
        const interviewModule = modules.find((module) => module.type === 'interview-module');

        if (interviewModule) {
            const questionsWithVideo = [...interviewModule.questions].filter(
                (question) => question.videos
            );
            return questionsWithVideo.map((question) => ({
                videoId: getInterviewVideoId(question.questionId),
                videoUrl: question.videos[resolution],
            }));
        } else return [];
    };

    const cultureFitVideos = getVideosFromCultureFit();
    const interviewModuleVideos = getVideosFromInterviewModule();

    return [...cultureFitVideos, ...interviewModuleVideos];
}
